import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import dynamic from 'next/dynamic';
import { mqFrom } from 'styles/styles';
import RichText from '@madebyconnor/rich-text-to-jsx';
import { Controller, useWatch, useFieldArray } from 'react-hook-form';
import ImageWithZoom from 'components/image-with-zoom';
import { isValidString, numToLetter } from 'utils/helpers';
import useEqualHeight from 'utils/useEqualHeight';

const DragDropContext = dynamic(() => import('react-beautiful-dnd').then((mod) => mod.DragDropContext), { ssr: false });
const Droppable = dynamic(() => import('react-beautiful-dnd').then((mod) => mod.Droppable), { ssr: false });
const Draggable = dynamic(() => import('react-beautiful-dnd').then((mod) => mod.Draggable), { ssr: false });

type MatchQuestionProps = {
  answers: any[];
  answersCurrentLocale: any[];
  answersDragDrop: any[];
  question: any;
  completed?: boolean;
  feedback?: string;
  questionIndex: number;
  control: any;
  image?: any;
  register?: any;
  name?: string;
  onAnswerChange?: any;
  questionId?: string;
  questionPoolId?: string;
};

const MatchQuestion: React.FC<MatchQuestionProps> = ({
  answers,
  answersCurrentLocale,
  question,
  completed,
  questionIndex,
  control,
  register,
  answersDragDrop,
  image,
  name = `questions.${questionIndex}.answers`,
  onAnswerChange = null,
  questionId,
  questionPoolId,
}) => {
  const { locale } = useRouter();
  const { fields, move } = useFieldArray({
    control,
    name: name,
  });

  const selectedAnswers = useWatch({
    control,
    name: name,
    defaultValue: fields,
  });

  const containerRef = useEqualHeight('box', selectedAnswers);

  useEffect(() => {
    if (questionId && onAnswerChange) {
      const checkAllAnswersCorrect = () => {
        return fields.every((field, index) => {
          // @ts-ignore
          const userAnswer = answersDragDrop[field.answer];
          const correctAnswer = answers[index];

          return userAnswer.initialOrder === correctAnswer.initialOrder;
        });
      };

      const correct = checkAllAnswersCorrect();
      // @ts-ignore
      const contentAnswerIds = fields.map((field) => answersDragDrop[field.answer]?.contentId).join(',');

      onAnswerChange({
        'content-question-id': questionId,
        'content-pool-id': questionPoolId,
        'content-answer-id': contentAnswerIds,
        correct: correct,
        name: name,
        answers: answers,
        selectedAnswers: selectedAnswers,
      });
    }
  }, [selectedAnswers]);

  const onDragEnd = (result) => {
    if (!result.destination) return;

    move(result.source.index, result.destination.index);
  };

  return (
    <StyledMatchQuestion>
      <div ref={containerRef}>
        {/* Question Label */}
        <div className="label">
          <span className="number">{questionIndex + 1}.</span>
          <div className="text-content">
            <RichText richText={question} />
          </div>
        </div>

        <div className="drag-drop">
          <div className="drag-drop-label statements">
            {answers.map((answer, index) => {
              const currentLocaleAnswer = answersCurrentLocale?.find(
                (answerLocale) => answerLocale.contentId === answer.contentId
              );

              return (
                <div className="flex statement box" key={index}>
                  <span className="letter">{numToLetter(index)}.&nbsp;</span>
                  <div className="label">
                    <span>
                      {locale !== 'en-US' && isValidString(currentLocaleAnswer.text)
                        ? currentLocaleAnswer.text
                        : answer.text}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          {/* @ts-ignore */}
          <DragDropContext onDragEnd={onDragEnd}>
            {/* @ts-ignore */}
            <Droppable droppableId="droppable">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="answers">
                  {fields.map((field, index) => {
                    // @ts-ignore
                    const answer = answersDragDrop ? answersDragDrop[field.answer] : [];
                    const draggableId = `answer-${questionIndex}-${index}`;
                    const isCorrect = completed && answer.initialOrder === answers[index].initialOrder;
                    const isWrong = completed && answer.initialOrder !== answers[index].initialOrder;
                    const correctAnswer = answers.find((ans) => ans.initialOrder === answers[index].initialOrder);

                    const currentLocaleAnswer = answersCurrentLocale?.find(
                      (answerLocale) => answerLocale.contentId === answer.contentId
                    );

                    const currentLocaleCorrectAnswer = answersCurrentLocale?.find(
                      (answerLocale) => answerLocale.contentId === correctAnswer.contentId
                    );

                    return (
                      <>
                        {/* @ts-ignore */}
                        <Draggable key={draggableId} draggableId={draggableId} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`box answer ${snapshot.isDragging ? 'dragging' : ''}
                          ${completed ? 'no-hover' : ''}
                          ${isCorrect ? 'is-correct' : ''} 
                          ${isWrong ? 'is-wrong' : ''}`}
                            >
                              <div {...provided.dragHandleProps}>
                                {isCorrect && <span className="checkmark-completed docon docon-check" />}
                                {isWrong && <span className="checkmark-completed docon docon-math-multiply" />}
                                <div
                                  className="text-box"
                                  // @ts-ignore
                                  defaultValue={`${field.answer}`}
                                  {...register(`${name}.${index}.answer`)}
                                >
                                  {locale !== 'en-US' && isValidString(currentLocaleAnswer.match)
                                    ? currentLocaleAnswer.match
                                    : answer.match}
                                </div>
                                <div className="drag-drop-icon-container">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="12"
                                    height="12"
                                    name="IconGrabHandle12"
                                  >
                                    <path
                                      fill="currentColor"
                                      fillRule="evenodd"
                                      d="M4 3a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm4 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM5 6a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm-3 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                                      clipRule="evenodd"
                                    ></path>
                                  </svg>
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                        {isWrong && (
                          <div className="text-correct">
                            {locale !== 'en-US' && isValidString(currentLocaleCorrectAnswer.match)
                              ? currentLocaleCorrectAnswer.match
                              : correctAnswer.match}
                          </div>
                        )}
                      </>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
        {/* {completed && feedback && <div className={`message `}>{feedback}</div>} */}
        {image && (
          <div className="image-wrapper">
            <ImageWithZoom src={image.url} alt={image.title} width={image.width} height={image.height} />
          </div>
        )}
      </div>
    </StyledMatchQuestion>
  );
};

export const StyledMatchQuestion = styled.ul`
  margin: 1.5rem 0 1rem 0;

  .label {
    display: inline-flex;

    .number {
      font-weight: 600;
      margin-right: 0.5rem;
    }
  }

  .text-content {
    p + p {
      margin-top: 0.5rem;
    }
  }

  .text-box {
    display: flex;
    flex-direction: row;
    /* align-items: center;
    justify-content: center; */
  }

  .image-wrapper {
    margin: 1.5rem 0 0 0;
    padding-left: 1.5rem;
    width: 100%;
  }

  .drag-drop {
    display: flex;
    width: 100%;
    margin-top: 1rem;
    padding-left: 1rem;
  }

  .drag-drop-label,
  .answers {
    display: flex;
    flex-direction: column;
    /* row-gap: 0.5rem; */
  }

  .drag-drop-label {
    display: flex;
    flex-direction: column;
    width: 100%;

    ${mqFrom.tablet} {
      margin: 0 4rem 0 3rem;
    }

    .label {
      border: 1px solid var(--border);
      padding: 0.5rem;
      /* min-height: 98px; */
      display: flex;
      align-items: center;
      width: 100%;
    }
  }

  .drag-container {
    display: flex;
    /* column-gap: 2rem; */
  }

  label {
    display: flex;
    align-items: center;
    width: 100%;
    cursor: pointer;
  }

  .hidden-radio {
    opacity: 0;
    position: absolute;
    left: -9999px;
  }

  .answers {
    width: 100%;
  }

  .answer {
    margin: 0.5rem 0;
    /* margin-bottom: 0.5rem; */
    position: relative;
    display: flex;
    align-items: center;
    background-color: var(--body-background-medium);
    border: 1px dashed var(--border);
    cursor: pointer;
    width: 100%;
    text-align: center;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    /* height: 100%; */
    /* height: 98px; */

    ${mqFrom.tablet} {
      background-color: transparent;
    }

    &:hover {
      background-color: transparent;
      border-color: transparent;
      box-shadow: 0 0 5px 0 var(--box-shadow-light);

      .drag-drop-icon-container {
        display: block;
      }
    }

    div:not(.drag-drop-icon-container) {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      margin: 10px 0 0.5rem 1.5rem;
      padding: 0 1rem 0 0;
      text-align: left;
    }

    .order {
      margin-right: 1rem;
      background: var(--primary-base);
    }

    > .label {
      flex: 1 1 0;
    }

    .message {
      font-weight: 600;
      margin: 0.25rem 0 0.5rem 3rem;
      padding-left: 1rem;
    }

    &.selected {
      background-color: var(--primary-background);
      border-color: var(--primary-base);

      .radio {
        border: 1px solid var(--primary-base);

        &:before {
          content: '';
          width: 6px;
          height: 6px;
          background: var(--primary-base);
          border-radius: 50%;
        }
      }
    }

    &.is-correct {
      background-color: var(--success-background);
      border-color: var(--success-base);
      margin-bottom: 0;

      .checkmark-completed {
        color: var(--success-base);
      }

      div:not(.drag-drop-icon-container) {
        padding: 0 0 0 0;
        margin: 10px 0 0.5rem 1rem;
      }

      .message {
        color: var(--success-base);
      }
    }

    &.is-wrong {
      background-color: var(--danger-background);
      border-color: var(--danger-base);
      margin-bottom: 0;

      .checkmark-completed {
        color: var(--danger-base);
      }

      div:not(.drag-drop-icon-container) {
        padding: 0 0 0 0;
        margin: 10px 0 0.5rem 1rem;
      }

      .message {
        color: var(--danger-base);
      }
    }
  }

  .message {
    font-weight: 600;
    margin: 0.25rem 0 0.5rem 3rem;
    padding-left: 4rem;

    &.is-correct {
      color: var(--success-base);
    }

    &.is-wrong {
      color: var(--danger-base);
    }
  }

  .statements,
  .answers {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    /* row-gap: 10px; */
  }

  /* .placeholder {
    margin-bottom: 10px;
  } */

  .flex {
    display: flex;
    position: relative;
    margin: 0.5rem 0;

    &::before {
      content: '';
      display: block;
      width: 4rem;
      height: 2px;
      background-color: var(--border);
      position: absolute;
      right: -4rem;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  .letter {
    display: flex;
    align-items: center;
    margin-right: 0.4rem;
  }

  .text-correct {
    padding: 0.5rem 0.5rem 0 3rem;
    color: var(--success-base);
    font-weight: 600;
  }
`;

export default MatchQuestion;
